/**
 * Learn more about theme events at
 * https://help.switchthemes.co/baseline/developer/theme-events/overview.html
 */

window.theme = window.theme || {};

window.theme.events = {
  version: '1.0',
};

// ──────────────────────────────────────────────────────────────────────
// _purchase_id: unique identifier per "Add to Cart" event.
//
// Injected as a property into EVERY line item of the submit (with `_`
// prefix → Shopify hides it from the customer) and substituted into
// any cart attribute name containing the __PID__ placeholder. This
// lets us namespace cart attributes so two identical products (two
// rings with different engravings, two twin kits with different
// resizings) don't collide on the same attribute key.
//
// Inputs that receive the pid:
//   - Any ENABLED input with [data-purchase-id-value]  → value = pid
//   - Any input whose name contains __PID__            → name replace
//
// Each picker (variant-picker / bundle-variant-picker / engraving-form)
// is responsible for enabling (disabled=false) its inputs at the right
// moment. Here we only set values and replace placeholders.
//
// NOTE: we iterate form.elements (not form.querySelectorAll) because
// the pickers declare their inputs OUTSIDE the <form> and associate
// them via the form="product-form-XXX" attribute. form.elements
// includes those external inputs; querySelectorAll only returns DOM
// descendants of the form.
// ──────────────────────────────────────────────────────────────────────
document.addEventListener(
  'submit',
  (e) => {
    const form = e.target;
    if (!form || !form.matches('form[action*="/cart/add"]')) return;

    const pid = 'p-' + Math.random().toString(36).slice(2, 8);

    for (const el of form.elements) {
      if (!el || el.disabled) continue;
      if (el.hasAttribute && el.hasAttribute('data-purchase-id-value')) {
        el.value = pid;
      }
      if (el.name && el.name.indexOf('__PID__') !== -1) {
        el.name = el.name.replace(/__PID__/g, pid);
      }
    }

    // Second pass: body-level inputs injected by engraving-form.liquid's
    // injectInput() are appended to document.body WITHOUT a form= attribute,
    // so they are NOT in form.elements. We process them separately so:
    //   1. items[1]properties[_purchase_id] gets the pid (→ loxRemoveServiceOnly
    //      can find and clear the cart attribute when Engrave It is deleted).
    //   2. attributes[... (#__PID__)] get the placeholder replaced so the cart
    //      attribute key is namespaced to this specific purchase event.
    document.querySelectorAll('[data-engraving-injected]').forEach((el) => {
      if (el.disabled) return;
      if (el.getAttribute('data-purchase-id-value') !== null) el.value = pid;
      if (el.name && el.name.indexOf('__PID__') !== -1) {
        el.name = el.name.replace(/__PID__/g, pid);
      }
    });
  },
  true // capture phase, runs before the theme's bubble-phase handlers
);

// ──────────────────────────────────────────────────────────────────────
// loxCleanupOrphanCartAttributes: scans cart attributes and removes
// any whose key contains a #pid that doesn't match any current line
// item. Defense against scenarios where the customer removes items
// through a path that does NOT use loxRemoveCartItemByKey (direct
// /cart page, third-party app, cart abandonment and return days later).
//
// Runs automatically:
//   - On DOMContentLoaded (any page load)
//   - On cart_changed (when CartWatcher detects external changes)
// ──────────────────────────────────────────────────────────────────────
window.loxCleanupOrphanCartAttributes = async function () {
  try {
    const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
    const validPids = new Set();
    for (const item of cart.items || []) {
      const pid = (item.properties || {})._purchase_id;
      if (pid) validPids.add(pid);
    }
    const toRemove = {};
    for (const key of Object.keys(cart.attributes || {})) {
      const m = key.match(/#(p-[a-z0-9]+)/);
      if (m && !validPids.has(m[1])) {
        toRemove[key] = '';
      }
      // Also clear attributes whose pid placeholder was never replaced
      // (happens when theme-events.js wasn't fully loaded at submit time).
      // These are permanently orphaned — no cleanup mechanism can match them
      // by pid since the key contains the literal string "__PID__".
      if (key.includes('__PID__')) {
        toRemove[key] = '';
      }
    }
    if (Object.keys(toRemove).length === 0) return;
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: toRemove }),
    });
  } catch (e) {
    console.warn('[Lox] orphan cart attribute cleanup failed:', e);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.loxCleanupOrphanCartAttributes);
} else {
  window.loxCleanupOrphanCartAttributes();
}
window.addEventListener('cart_changed', window.loxCleanupOrphanCartAttributes);

// ──────────────────────────────────────────────────────────────────────
// loxRemoveCartItemByKey: smart removal that branches by item type.
//
//   - Trash on a PARENT (ring / bundle): removes the parent AND all
//     associated services in the same _purchase_id (engraving, resizing)
//     AND clears every cart attribute containing that pid. The whole
//     purchase event vanishes.
//
//   - Trash on a SERVICE (Custom Resizing, Engrave It, Engrave Them):
//     removes ONLY that service line item, clears ONLY the cart
//     attribute matching that specific service, AND strips the marker
//     properties that the service had written onto the parent ring:
//       • Engrave It / Engrave Them → clears `_engraved` from parent
//       • Custom Resizing           → clears `_resized` from parent
//     The parent ring stays in the cart, just without the service marker.
//
//   - If the target item has no _purchase_id (legacy items pre-May 2026),
//     it performs a plain removal and touches nothing else.
// ──────────────────────────────────────────────────────────────────────
window.LOX_SERVICE_HANDLES = new Set(['resizing', 'engrave-it', 'engrave-them']);

function loxEscapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Returns the list of property keys to remove from the parent ring
// when a service is deleted. Keys must match exactly what's on the ring.
function loxPropsToCleanFromParent(service) {
  const handle = service.handle;
  if (handle === 'engrave-it' || handle === 'engrave-them') {
    // Engraving   → customer-visible property on single rings ("Forever" · Gothicus).
    //               PostCo Rule 2 uses this key to mark engraved items non-returnable.
    // Engraving 1 / Engraving 2 → customer-visible properties on twin kits when
    //   both rings are engraved (Engrave Them). Keys absent on the parent are
    //   silently skipped by the caller.
    return ['Engraving', 'Engraving 1', 'Engraving 2'];
  }
  if (handle === 'resizing') {
    // Resizing → customer-visible property on single rings.
    //            PostCo Rule 2 uses this key to mark resized items non-returnable.
    // Resizing (Ring A) → customer-visible property on twin kits, keyed by
    //   component name so only the correct component's property is cleared.
    //   For single rings, `Resizing For` equals the product title but the
    //   property is plain `Resizing` — the keyed variant won't exist, no-op.
    const resizingFor = (service.properties || {})['Resizing For'];
    const keys = ['Resizing'];
    if (resizingFor) keys.push(`Resizing (${resizingFor})`);
    return keys;
  }
  return [];
}

// Returns a RegExp that matches the cart attribute key associated with
// the given service (so we don't accidentally clear sibling attributes
// from the same purchase event, e.g. the other ring's resizing in a
// Twin Kit, or the resizing attribute when removing the engraving).
function loxAttrRegexForService(service, pid) {
  const handle = service.handle;
  const props = service.properties || {};
  const pidEsc = loxEscapeRegex(pid);
  // Cart attribute key format (May 2026):
  //   Single ring:  "{title} - {Resizing|Engraving} (#pid)"
  //   Twin Kit:     "{title} - {Resizing|Engraving} (Twin Kit) (#pid)"
  // The optional " (Twin Kit)" segment is captured by `(?: \\(Twin Kit\\))?`.
  if (handle === 'engrave-it' || handle === 'engrave-them') {
    // Matches both:
    //   "Ring - Engraving (#pid)"              → single ring, Engrave It
    //   "Ring - Engraving (Twin Kit) (#pid)"   → twin kit single engraving
    //   "Ring - Engraving 1 (Twin Kit) (#pid)" → twin kit, Engrave Them (numbered)
    //   "Ring - Engraving 2 (Twin Kit) (#pid)" → twin kit, Engrave Them (numbered)
    return new RegExp(` - Engraving(?: \\d+)?(?: \\(Twin Kit\\))? \\(#${pidEsc}\\)$`);
  }
  if (handle === 'resizing') {
    // A Twin Kit can have TWO Custom Resizing services with the same pid
    // (one per component). We need `Resizing For` to disambiguate which
    // cart attribute belongs to the service being removed.
    const target = props['Resizing For'];
    if (target) {
      return new RegExp(`^${loxEscapeRegex(target)} - Resizing(?: \\(Twin Kit\\))? \\(#${pidEsc}\\)$`);
    }
    return new RegExp(` - Resizing(?: \\(Twin Kit\\))? \\(#${pidEsc}\\)$`);
  }
  return null;
}

async function loxRemoveServiceOnly(cart, service, serviceKey, pid) {
  const parent = cart.items.find((i) =>
    i.key !== serviceKey &&
    !window.LOX_SERVICE_HANDLES.has(i.handle) &&
    (i.properties || {})._purchase_id === pid
  );

  const attrPattern = loxAttrRegexForService(service, pid);
  const attributes = {};
  if (attrPattern) {
    Object.keys(cart.attributes || {}).forEach((k) => {
      if (attrPattern.test(k)) attributes[k] = '';
    });
  }

  // Step 1: drop the service line item + clear its cart attribute(s).
   const response = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates: { [serviceKey]: 0 }, attributes }),
  });

  const updatedCart = await response.json();
    window.cartObject = updatedCart;
    await window.validateGWP();

  // Step 2: strip the service marker property from the parent ring.
  // Uses /cart/change.js because /cart/update.js doesn't accept properties.
  // Only fires if there's actually a key to remove.
  if (parent) {
    const cleanKeys = loxPropsToCleanFromParent(service);
    const newProps = { ...(parent.properties || {}) };
    let anyChange = false;
    cleanKeys.forEach((k) => {
      if (k in newProps) {
        delete newProps[k];
        anyChange = true;
      }
    });
    if (anyChange) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parent.key,
          quantity: parent.quantity,
          properties: newProps,
        }),
      });
    }
  }
}

async function loxRemoveFullPurchaseEvent(cart, itemKey, pid) {
  const updates = { [itemKey]: 0 };
  const attributes = {};

  if (pid) {
    cart.items.forEach((it) => {
      if (it.key !== itemKey && (it.properties || {})._purchase_id === pid) {
        updates[it.key] = 0;
      }
    });
    Object.keys(cart.attributes || {}).forEach((k) => {
      if (k.includes(pid)) attributes[k] = '';
    });
  }


  const response = await fetch('/cart/update.js', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ updates, attributes }),
});

const updatedCart = await response.json();

 window.cartObject = updatedCart;
 await window.validateGWP();
}

window.loxRemoveCartItemByKey = async function (itemKey) {
  let cart = window.cartObject; 
  if (!cart?.items?.find) {
    // cartObject is corrupt/stale refetch once
    cart = await window.updateCartObject();
  }
  if (!cart?.items) return;
  
  const target = cart.items.find((i) => i.key === itemKey);

  if (!target) { 
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: itemKey, quantity: 0 }),
    });
    const updatedCart = await response.json();
    window.cartObject = updatedCart;
    await window.validateGWP();
    document.dispatchEvent(new CustomEvent('theme:update:cart'));
    return;
  }

  const pid = (target.properties || {})._purchase_id;
  const isService = window.LOX_SERVICE_HANDLES.has(target.handle);

  if (isService && pid) {
    await loxRemoveServiceOnly(cart, target, itemKey, pid);
  } else {
    await loxRemoveFullPurchaseEvent(cart, itemKey, pid);
  }
  
  document.dispatchEvent(new CustomEvent('theme:update:cart'));
};


document.addEventListener('theme:update:cart', async () => {
  document.dispatchEvent(new CustomEvent('baseline:cart:lock'));

  try {
    const response = await fetch(
      `${window.location.pathname}?sections=cart-items,cart-footer,cart-item-count,cart-live-region`
    );
    const data = await response.json();

    if (!data.status) {
      document.body.dispatchEvent(
        new CustomEvent('baseline:cart:update', {
          bubbles: true,
          detail: {
            response: {
              sections: {
                ...data,
              },
            },
          },
        })
      );
    }
  } catch (e) {
    console.error(e);
    document.getElementById('cart-errors').textContent =
      theme.strings.cartError;
    document.dispatchEvent(
      new CustomEvent('theme:cart:error:other', {
        detail: {
          message: theme.strings.cartError,
          error: e,
        },
      })
    );
  } finally {
    document.dispatchEvent(new CustomEvent('baseline:cart:unlock'));
  }
});

const getCartDrawerEl = () => {
  return document.querySelector('[data-cart-drawer]');
};

document.body.addEventListener('cart-will-open', async () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:opening', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.body.addEventListener('cart-is-open', async () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:open', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.body.addEventListener('cart-will-close', () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:closing', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.body.addEventListener('cart-is-closed', () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:closed', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.addEventListener('theme:open:cart-drawer', () => {
  if (
    window.theme.settings.cartType === 'drawer' &&
    Alpine.store('modals').modals.cart
  ) {
    Alpine.store('modals').closeAll();

    Alpine.store('modals').open('cart');
  }
});

document.addEventListener('theme:close:cart-drawer', () => {
  if (
    window.theme.settings.cartType === 'drawer' &&
    Alpine.store('modals').modals.cart
  ) {
    Alpine.store('modals').close('cart');
  }
});

const cartChangeEventHandler = (e) => {
  const { cartItemCount, itemsRootEl, lineItemEl, variantId, key } = e.detail;

  document.dispatchEvent(
    new CustomEvent('theme:cart:change', {
      detail: {
        type: e.type,
        cartItemCount,
        itemsRootEl,
        lineItemEl,
        variantId,
        key,
        originalDetail: e.detail,
      },
    })
  );
};

document.addEventListener('theme:product:add', cartChangeEventHandler);
document.addEventListener('theme:line-item:change', cartChangeEventHandler);

const cartErrorEventHandler = (e) => {
  document.dispatchEvent(
    new CustomEvent('theme:cart:error', {
      detail: {
        type: e.type,
        message: e.detail.message,
        originalDetail: e.detail,
      },
    })
  );
};

document.addEventListener(
  'theme:product:error:add-to-cart',
  cartErrorEventHandler
);

document.addEventListener('theme:line-item:error', cartErrorEventHandler);
document.addEventListener('theme:cart:error:other', cartErrorEventHandler);

/**
 * Set `window.themeEventsDebugMode` to `true` to log events
 * outside the theme editor
 */

if (Shopify.designMode || window.themeEventsDebugMode) {
  const themeEvents = [
    'theme:variant:change',
    'theme:product:add',
    'theme:line-item:change',
    'theme:cart:change',
    'theme:cart:update',
    'theme:cart-drawer:opening',
    'theme:cart-drawer:open',
    'theme:cart-drawer:closing',
    'theme:cart-drawer:closed',
    'theme:product:error:add-to-cart',
    'theme:line-item:error',
    'theme:cart:error:other',
    'theme:cart:error',
  ];

  const aggregateEvents = ['theme:cart:change', 'theme:cart:error'];

  for (const eventName of themeEvents) {
    let color;

    if (eventName.includes('error')) {
      color = '#ef4444';
    } else {
      color = '#22c55e';
    }

    const message = [
      `%c● %c${eventName}`,
      `color: ${color};`,
      'font-weight: bold;',
    ];

    if (aggregateEvents.includes(eventName)) {
      document.addEventListener(eventName, (e) => {
        console.groupCollapsed(...message);
        console.log(
          `Underlying event: %c${e.detail.type}`,
          'font-weight: bold;'
        );
        console.log('Event detail:', e.detail);
        console.groupEnd();
      });
    } else {
      document.addEventListener(eventName, (e) => {
        console.log(...message, e.detail);
      });
    }
  }
}

// Use when unicorn is ovewritting the cart structure, if removed it can be added to the cart logic
class CartWatcher {
  constructor() {
    this.storageKey = 'cart_cache';
    this.flagKey = 'has_special_items';
  }

  init() {
    const hasFlag = sessionStorage.getItem(this.flagKey) === 'true';
    this.observeCartChanges(hasFlag);
  }

  observeCartChanges(isAlreadyActive) {
    let isActive = isAlreadyActive;

    new PerformanceObserver((list) => {
      const entries = list.getEntries();

      const isAdding = entries.some(e => e.name.includes('/cart/add'));
      const isCartRequest = entries.some(e => /\/cart\//.test(e.name));

      if (isAdding || (isActive && isCartRequest)) {
        this.emitCartChanges().then(hasSpecial => {
          isActive = hasSpecial;
          sessionStorage.setItem(this.flagKey, hasSpecial);
        });
      }
    }).observe({ entryTypes: ["resource"] });
  }

  async fetchCart() {
    const res = await fetch(`${window.Shopify.routes.root}cart.js`);
    return res.json();
  }

  async emitCartChanges() {
    const newCart = await this.fetchCart();
    const oldCart = JSON.parse(localStorage.getItem(this.storageKey)) || { items: [] };

    // Fixed: detect child line items flexibly so the observer wakes up.
    const hasSpecialItems = newCart.items.some(i => {
      const title = i.product_title || i.title || "";
      return title.includes("Engrave It") || title.includes("Engrave Them") || title.toLowerCase().includes("resizing");
    });

    const onlyInLeft = (l, r) => l.filter(li => !r.some(ri => li.key == ri.key));
    let changes = {
      added: onlyInLeft(newCart.items, oldCart.items),
      removed: onlyInLeft(oldCart.items, newCart.items),
      currentItems: newCart.items
    };

    oldCart.items.forEach(oi => {
      const ni = newCart.items.find(i => i.key == oi.key && i.quantity != oi.quantity);
      if (ni) {
        let diff = ni.quantity - oi.quantity;
        let item = { ...ni, quantity: Math.abs(diff) };
        diff > 0 ? changes.added.push(item) : changes.removed.push(item);
      }
    });

    if (changes.added.length > 0 || changes.removed.length > 0) {
      window.dispatchEvent(new CustomEvent("cart_changed", { detail: changes }));
    }

    localStorage.setItem(this.storageKey, JSON.stringify(newCart));
    return hasSpecialItems;
  }
}

new CartWatcher().init();